import React from 'react';
import {
  Card,
  DataTable,
  Text,
  Badge,
  Button,
  InlineStack,
  EmptyState,
  Box,
} from '@shopify/polaris';
import api from '../../services/api';

export interface PaymentRecord {
  id: number;
  invoiceNumber: string;
  invoiceDate: string;
  totalAmount: number;
  totalAmountFormatted: string;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  paidAt?: string;
  periodStart?: string;
  periodEnd?: string;
}

interface BillingHistoryTableProps {
  payments: PaymentRecord[];
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

const statusBadgeTone: Record<string, 'success' | 'warning' | 'critical' | 'info'> = {
  paid: 'success',
  pending: 'warning',
  failed: 'critical',
  refunded: 'info',
};

export const BillingHistoryTable: React.FC<BillingHistoryTableProps> = ({
  payments,
  isLoading,
  onLoadMore,
  hasMore,
}) => {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDownloadInvoice = async (invoiceNumber: string) => {
    try {
      const response = await api.subscription.downloadInvoicePdf(invoiceNumber);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download invoice:', error);
    }
  };

  if (payments.length === 0 && !isLoading) {
    return (
      <Card>
        <EmptyState
          heading="No billing history"
          image=""
        >
          <p>Your payment history will appear here once you make your first payment.</p>
        </EmptyState>
      </Card>
    );
  }

  const rows = payments.map((payment) => [
    <Text as="span" key={`inv-${payment.id}`} variant="bodyMd">
      {payment.invoiceNumber}
    </Text>,
    formatDate(payment.invoiceDate),
    payment.periodStart && payment.periodEnd
      ? `${formatDate(payment.periodStart)} - ${formatDate(payment.periodEnd)}`
      : '-',
    payment.totalAmountFormatted,
    <Badge key={`status-${payment.id}`} tone={statusBadgeTone[payment.status]}>
      {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
    </Badge>,
    <InlineStack key={`actions-${payment.id}`} gap="200">
      <Button
        size="slim"
        onClick={() => handleDownloadInvoice(payment.invoiceNumber)}
      >
        Download
      </Button>
    </InlineStack>,
  ]);

  return (
    <Card>
      <DataTable
        columnContentTypes={[
          'text',
          'text',
          'text',
          'numeric',
          'text',
          'text',
        ]}
        headings={[
          'Invoice',
          'Date',
          'Period',
          'Amount',
          'Status',
          'Actions',
        ]}
        rows={rows}
        hoverable
      />
      {hasMore && onLoadMore && (
        <Box paddingBlockStart="400" paddingInlineStart="400" paddingInlineEnd="400" paddingBlockEnd="400">
          <InlineStack align="center">
            <Button onClick={onLoadMore} loading={isLoading}>
              Load More
            </Button>
          </InlineStack>
        </Box>
      )}
    </Card>
  );
};

export default BillingHistoryTable;
